import { FC, useMemo, useState } from 'react';
import { InputNumber, globalToastService } from '@repo/atomic-components';
import { InputNumberChangeEvent } from 'primereact/inputnumber';
import { useLazyPostEditOrgCaseRetentionTimesQuery } from '@/api';
import { GreenButton } from '@/ui/components/GreenButton';
import { DashboardSection } from './DashboardSection';

const inputClass =
  'border-fletchers-gray-110! hover:border-fletchers-gray-110! focus:border-fletchers-gray-110! h-8! w-full text-[14px]/[20px]! focus:outline-none!';

const labelClass = 'text-fletchers-gray-200 block text-[14px]/[20px] font-[500]';

// Whole weeks, zero or more — retention periods can't be negative.
const isValidWeeks = (value: number | null): value is number =>
  typeof value === 'number' && Number.isInteger(value) && value >= 0;

interface DataRetentionSectionProps {
  className?: string;
}

/**
 * Changes data-retention periods for the *currently switched-into* org via
 * `admin-utils/edit-org-case-retention-times`. Lives beneath the org switcher
 * in the left column. Only accepts whole numbers of weeks, zero or more.
 */
export const DataRetentionSection: FC<DataRetentionSectionProps> = ({ className }) => {
  const [postCaseClosure, setPostCaseClosure] = useState<number | null>(null);
  const [postFirstUpload, setPostFirstUpload] = useState<number | null>(null);
  const [triggerEditRetention, { isFetching }] = useLazyPostEditOrgCaseRetentionTimesQuery();

  const canSubmit = useMemo(
    () => isValidWeeks(postCaseClosure) && isValidWeeks(postFirstUpload),
    [postCaseClosure, postFirstUpload],
  );

  const handleSave = () => {
    if (!isValidWeeks(postCaseClosure) || !isValidWeeks(postFirstUpload)) return;
    triggerEditRetention({
      retentionInWeeksPostCaseClosure: postCaseClosure,
      retentionInWeeksPostFirstRecordUpload: postFirstUpload,
    })
      .unwrap()
      .then(() => {
        globalToastService.show({
          severity: 'success',
          summary: 'Retention updated',
          detail: 'Data retention periods updated for the current organisation.',
        });
      })
      .catch((error) => {
        console.error('Failed to update retention times:', error);
        globalToastService.show({ severity: 'error', summary: 'Error', detail: 'Failed to update retention times.' });
      });
  };

  return (
    <DashboardSection
      title="Change data retention"
      description="Update retention periods for your current organisation. Whole weeks, zero or more."
      className={className}
    >
      <div className="flex flex-col gap-4">
        <div className="flex flex-col gap-2">
          <label htmlFor="retention-post-closure" className={labelClass}>
            Retention post case closure (weeks)
          </label>
          <InputNumber
            inputId="retention-post-closure"
            value={postCaseClosure}
            onChange={(e: InputNumberChangeEvent) => setPostCaseClosure((e.value ?? null) as number | null)}
            min={0}
            placeholder="12"
            inputClassName={inputClass}
            className="w-full max-w-xs"
          />
        </div>

        <div className="flex flex-col gap-2">
          <label htmlFor="retention-post-upload" className={labelClass}>
            Retention post first record upload (weeks)
          </label>
          <InputNumber
            inputId="retention-post-upload"
            value={postFirstUpload}
            onChange={(e: InputNumberChangeEvent) => setPostFirstUpload((e.value ?? null) as number | null)}
            min={0}
            placeholder="26"
            inputClassName={inputClass}
            className="w-full max-w-xs"
          />
        </div>
      </div>

      <div className="mt-auto flex justify-start pt-2">
        <GreenButton
          label="Update retention"
          icon="pi pi-clock"
          disabled={!canSubmit || isFetching}
          loading={isFetching}
          onClick={handleSave}
          style={{ height: 36 }}
        />
      </div>
    </DashboardSection>
  );
};
